/**
 * Generated by orval 🍺
 * Do not edit manually.
 * ILP Content Delivery API
 * API for delivering educational content including course structures and glossaries
 * OpenAPI spec version: v1
 */
import useSwr from "swr";
import type { Arguments, Key, SWRConfiguration } from "swr";

import useSWRMutation from "swr/mutation";
import type { SWRMutationConfiguration } from "swr/mutation";

import type {
  CompletionQuizQuestions,
  CompletionQuizResult,
  CompletionQuizRun,
  CompletionQuizStartError,
  Error,
} from "../model";

import { apiFetch } from "../../apiFetch";

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

export const getStartCourseCompletionQuizUrl = (enrollmentId: string) => {
  return `/api/v1/enrollments/${enrollmentId}/course_completion_quiz/start`;
};

/**
 * Starts a run and freezes its questions, or returns the open run. Refused with the reason (locked, waiting, passed, failed_final, no_quiz, no_questions) when no run may be started.
 * @summary Start a run through the course completion quiz
 */
export const startCourseCompletionQuiz = async (
  enrollmentId: string,
  options?: Parameters<typeof apiFetch>[1]
): Promise<CompletionQuizRun> => {
  return apiFetch<CompletionQuizRun>(
    getStartCourseCompletionQuizUrl(enrollmentId),
    {
      ...options,
      method: "POST",
    }
  );
};

export const getStartCourseCompletionQuizMutationFetcher = (
  enrollmentId: string,
  options?: SecondParameter<typeof apiFetch>
) => {
  return (_: Key, __: { arg: Arguments }) => {
    return startCourseCompletionQuiz(enrollmentId, options);
  };
};
export const getStartCourseCompletionQuizMutationKey = (enrollmentId: string) =>
  [`/api/v1/enrollments/${enrollmentId}/course_completion_quiz/start`] as const;

export type StartCourseCompletionQuizMutationResult = NonNullable<
  Awaited<ReturnType<typeof startCourseCompletionQuiz>>
>;

/**
 * @summary Start a run through the course completion quiz
 */
export const useStartCourseCompletionQuiz = <TError = CompletionQuizStartError>(
  enrollmentId: string,
  options?: {
    swr?: SWRMutationConfiguration<
      Awaited<ReturnType<typeof startCourseCompletionQuiz>>,
      TError,
      Key,
      Arguments,
      Awaited<ReturnType<typeof startCourseCompletionQuiz>>
    > & { swrKey?: string };
    request?: SecondParameter<typeof apiFetch>;
  }
) => {
  const { swr: swrOptions, request: requestOptions } = options ?? {};

  const swrKey =
    swrOptions?.swrKey ?? getStartCourseCompletionQuizMutationKey(enrollmentId);
  const swrFn = getStartCourseCompletionQuizMutationFetcher(
    enrollmentId,
    requestOptions
  );

  const query = useSWRMutation(swrKey, swrFn, swrOptions);

  return {
    swrKey,
    ...query,
  };
};
export const getGetCourseCompletionQuizQuestionsUrl = (
  enrollmentId: string
) => {
  return `/api/v1/enrollments/${enrollmentId}/course_completion_quiz/questions`;
};

/**
 * The frozen questions of the open run without their answer key, with the answer given so far (no verdict) so that a run can be resumed.
 * @summary Questions of the open run
 */
export const getCourseCompletionQuizQuestions = async (
  enrollmentId: string,
  options?: Parameters<typeof apiFetch>[1]
): Promise<CompletionQuizQuestions> => {
  return apiFetch<CompletionQuizQuestions>(
    getGetCourseCompletionQuizQuestionsUrl(enrollmentId),
    {
      ...options,
      method: "GET",
    }
  );
};

export const getGetCourseCompletionQuizQuestionsKey = (enrollmentId: string) =>
  [
    `/api/v1/enrollments/${enrollmentId}/course_completion_quiz/questions`,
  ] as const;

export type GetCourseCompletionQuizQuestionsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getCourseCompletionQuizQuestions>>
>;

/**
 * @summary Questions of the open run
 */
export const useGetCourseCompletionQuizQuestions = <TError = Error>(
  enrollmentId: string,
  options?: {
    swr?: SWRConfiguration<
      Awaited<ReturnType<typeof getCourseCompletionQuizQuestions>>,
      TError
    > & { swrKey?: Key; enabled?: boolean };
    request?: SecondParameter<typeof apiFetch>;
  }
) => {
  const { swr: swrOptions, request: requestOptions } = options ?? {};

  const isEnabled =
    swrOptions?.enabled !== false &&
    enrollmentId !== null &&
    enrollmentId !== undefined;
  const swrKey =
    swrOptions?.swrKey ??
    (() =>
      isEnabled ? getGetCourseCompletionQuizQuestionsKey(enrollmentId) : null);
  const swrFn = () =>
    getCourseCompletionQuizQuestions(enrollmentId, requestOptions);

  const query = useSwr<Awaited<ReturnType<typeof swrFn>>, TError>(
    swrKey,
    swrFn,
    swrOptions
  );

  return {
    swrKey,
    ...query,
  };
};
export const getGetCourseCompletionQuizResultUrl = (enrollmentId: string) => {
  return `/api/v1/enrollments/${enrollmentId}/course_completion_quiz/result`;
};

/**
 * The score of the last finished run and what comes next. The review of every question (`questions`) is only included once the quiz is passed or out of attempts.
 * @summary Result of the last finished run
 */
export const getCourseCompletionQuizResult = async (
  enrollmentId: string,
  options?: Parameters<typeof apiFetch>[1]
): Promise<CompletionQuizResult> => {
  return apiFetch<CompletionQuizResult>(
    getGetCourseCompletionQuizResultUrl(enrollmentId),
    {
      ...options,
      method: "GET",
    }
  );
};

export const getGetCourseCompletionQuizResultKey = (enrollmentId: string) =>
  [
    `/api/v1/enrollments/${enrollmentId}/course_completion_quiz/result`,
  ] as const;

export type GetCourseCompletionQuizResultQueryResult = NonNullable<
  Awaited<ReturnType<typeof getCourseCompletionQuizResult>>
>;

/**
 * @summary Result of the last finished run
 */
export const useGetCourseCompletionQuizResult = <TError = Error>(
  enrollmentId: string,
  options?: {
    swr?: SWRConfiguration<
      Awaited<ReturnType<typeof getCourseCompletionQuizResult>>,
      TError
    > & { swrKey?: Key; enabled?: boolean };
    request?: SecondParameter<typeof apiFetch>;
  }
) => {
  const { swr: swrOptions, request: requestOptions } = options ?? {};

  const isEnabled =
    swrOptions?.enabled !== false &&
    enrollmentId !== null &&
    enrollmentId !== undefined;
  const swrKey =
    swrOptions?.swrKey ??
    (() =>
      isEnabled ? getGetCourseCompletionQuizResultKey(enrollmentId) : null);
  const swrFn = () =>
    getCourseCompletionQuizResult(enrollmentId, requestOptions);

  const query = useSwr<Awaited<ReturnType<typeof swrFn>>, TError>(
    swrKey,
    swrFn,
    swrOptions
  );

  return {
    swrKey,
    ...query,
  };
};
